<template>
    <div data-backdrop="static" data-keyboard="false" class="modal fade" id="carrier-destination-price-form-modal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title page-title" v-html="modalTitle"></h5>
                </div>
                <form @submit.prevent="submit()" class="form-horizontal">
                    <div class="modal-body">
                        <div class="form-group row">
                            <label v-if="carrier.chargesBy === 'weight'" for="min-weight" class="col-4 control-label">
                                <span v-html="$t('CarrierDestinationPriceFormModal.minWeight')"/>
                                <RedAsterisk/>
                            </label>
                            <label v-else for="min-quantity" class="col-4 control-label">
                                <span v-html="$t('CarrierDestinationPriceFormModal.minQuantity')"/>
                                <RedAsterisk/>
                            </label>
                            <label v-if="carrier.chargesBy === 'weight'" for="max-weight" class="col-4 control-label">
                                <span v-html="$t('CarrierDestinationPriceFormModal.maxWeight')"/>
                                <RedAsterisk/>
                                <InfoCircle :tooltip="$t('CarrierDestinationPriceFormModal.carrierMaxWeight', { maxWeight: this.carrierDestination.carrier.maxWeight })"/>
                            </label>
                            <label v-else for="max-quantity" class="col-4 control-label">
                                <span v-html="$t('CarrierDestinationPriceFormModal.maxQuantity')"/>
                                <RedAsterisk/>
                            </label>
                            <label for="price" class="col-4 control-label">
                                <span v-html="$t('CarrierDestinationPriceFormModal.price')"/>
                                <RedAsterisk/>
                            </label>
                            <div v-if="carrier.chargesBy === 'weight'" class="col-4">
                                <div class="input-group">
                                    <input id="min-weight" :placeholder="$t('CarrierDestinationPriceFormModal.required')" type="number"
                                           class="form-control" required min="1" :max="maxForMinWeight"
                                           v-model="carrierDestinationPrice.minWeight" ref="minWeightInput"/>
                                    <div class="input-group-append">
                                        <span class="input-group-text" v-html="'g'"/>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="col-4">
                                <input id="min-quantity" :placeholder="$t('CarrierDestinationPriceFormModal.required')" type="number"
                                       class="form-control" required min="1" :max="parseInt(carrierDestinationPrice.maxQuantity) - 1"
                                       v-model="carrierDestinationPrice.minQuantity" ref="minQuantityInput"/>
                            </div>
                            <div v-if="carrier.chargesBy === 'weight'" class="col-4">
                                <div class="input-group">
                                    <input id="max-weight" :placeholder="$t('CarrierDestinationPriceFormModal.required')" :min="minForMaxWeight"
                                           type="number" required class="form-control" :max="carrierDestination.carrier.maxWeight"
                                           v-model="carrierDestinationPrice.maxWeight"/>
                                    <div class="input-group-append">
                                        <span class="input-group-text" v-html="'g'"/>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="col-4">
                                <input id="max-quantity" :placeholder="$t('CarrierDestinationPriceFormModal.required')" type="number"
                                       class="form-control" required :min="parseInt(carrierDestinationPrice.minQuantity) + 1"
                                       v-model="carrierDestinationPrice.maxQuantity" ref="maxQuantityInput"/>
                            </div>
                            <div class="col-4">
                                <div class="input-group">
                                    <input id="price" :placeholder="$t('CarrierDestinationPriceFormModal.required')" min="0.01" step="0.01"
                                           type="number" required class="form-control" v-model="carrierDestinationPrice.price"/>
                                    <div class="input-group-append">
                                        <span class="input-group-text" v-html="currency"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="carrier.chargesBy === 'quantity'" class="row form-group">
                            <div class="col-4">
                                <label class="control-label" v-html="$t('CarrierDestinationPriceFormModal.hasStartingPrice')"/>
                                <br/>
                                <toggle-button :value="hasStartingPrice" :sync="true" v-model="hasStartingPrice"/>
                            </div>
                            <transition name="fade-in">
                                <div v-if="hasStartingPrice" class="col-4">
                                    <label class="control-label" v-html="$t('CarrierDestinationPriceFormModal.startingPriceAmount')"/>
                                    <div class="input-group">
                                        <input id="starting-price" :placeholder="$t('CarrierDestinationPriceFormModal.required')" min="0.01" step="0.01"
                                               type="number" required class="form-control" v-model="carrierDestinationPrice.startingPrice"/>
                                        <div class="input-group-append">
                                            <span class="input-group-text" v-html="currency"/>
                                        </div>
                                    </div>
                                </div>
                            </transition>
                            <transition name="fade-in">
                                <div v-if="hasStartingPrice" class="col-4">
                                    <label class="control-label" v-html="$t('CarrierDestinationPriceFormModal.firstProductIsIncluded')"/>
                                    <br/>
                                    <toggle-button :value="carrierDestinationPrice.firstProductIncludedInStartingPrice"
                                                   :sync="true" v-model="carrierDestinationPrice.firstProductIncludedInStartingPrice"/>
                                </div>
                            </transition>
                        </div>
                        <div v-if="conflict || !conflict && explicitRangeMeaning">
                            <hr/>
                            <div class="row mb-3">
                                <div class="col-12 text-center" v-html="explicitRangeMeaning"></div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <PriceRangeConflict :carrier-destination="carrierDestination" @conflict="setConflict"
                                                        :carrier-destination-price="carrierDestinationPrice"/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer flex justify-content-between">
                        <button :disabled="loading" type="button" class="btn btn-secondary" data-dismiss="modal" v-html="$t('CarrierDestinationPriceFormModal.close')"/>
                        <SubmitBtn :disabled="formInvalid" classes="btn btn-primary" :text="modalMainAction" :loading="loading"/>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import CarrierDestinationPrice from "../../../../../../classes/Billing/Carrier/CarrierDestinationPrice";
import SubmitBtn from "../../../../forms/SubmitBtn";
import PriceRangeConflict from "./PriceRangeConflict";
import RedAsterisk from "../../../../forms/RedAsterisk";
import InfoCircle from "../../../../misc/InfoCircle";

export default {
    name: "CarrierDestinationPriceFormModal",
    components: {InfoCircle, RedAsterisk, PriceRangeConflict, SubmitBtn},
    props: {
        carrier: {
            required: true,
        },
        carrierDestination: {
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            carrierDestinationPrice: new CarrierDestinationPrice(),
            mode: "create",
            conflict: false,
            hasStartingPrice: false,
        };
    },
    computed: {
        ...mapGetters("currencies", {
            currencies: "currencies",
        }),
        modalTitle() {
            return this.mode === "create" ? this.$t("CarrierDestinationPriceFormModal.addCarrierDestinationPrice") : this.$t("CarrierDestinationPriceFormModal.editCarrierDestinationPrice");
        },
        modalMainAction() {
            return this.mode === "create" ? this.$t("CarrierDestinationPriceFormModal.add") : this.$t("CarrierDestinationPriceFormModal.edit");
        },
        currency() {
            if (!this.currencies) {
                return "?";
            }
            const currency = this.currencies.find(c => c.alpha3ISO === this.carrier.currency);
            return currency ? currency.symbol : "?";
        },
        maxForMinWeight() {
            if (this.carrierDestinationPrice.maxWeight && typeof this.carrierDestinationPrice.maxWeight === "number") {
                return this.carrierDestination.carrier.maxWeight;
            } else {
                return parseInt(this.carrierDestinationPrice.maxWeight) - 1;
            }
        },
        minForMaxWeight() {
            if (this.carrierDestinationPrice.minWeight && typeof this.carrierDestinationPrice.minWeight === "number") {
                return 1;
            } else {
                return parseInt(this.carrierDestinationPrice.minWeight) + 1;
            }
        },
        explicitRangeMeaning() {
            let rangeMeaning = "";
            if (this.carrier.chargesBy === "weight" && (!this.carrierDestinationPrice.minWeight || !this.carrierDestinationPrice.maxWeight) ||
                (this.carrier.chargesBy === "lot" || this.carrier.chargesBy === "quantity") && (!this.carrierDestinationPrice.maxQuantity || !this.carrierDestinationPrice.maxQuantity) ||
                !this.carrierDestinationPrice.price) {
                return rangeMeaning;
            }
            if (this.carrier.chargesBy === "weight") {
                rangeMeaning += this.$t("CarrierDestinationPriceFormModal.forAPackageWeighingBetweenMinAndMaxGrams", this.carrierDestinationPrice);
            } else {
                rangeMeaning += this.$t("CarrierDestinationPriceFormModal.forAPackageContainingBetweenMinAndMaxProducts", this.carrierDestinationPrice);
            }
            rangeMeaning += `, ${this.$t("CarrierDestinationPriceFormModal.deliveryPriceWillBe")}<br/>`;
            rangeMeaning += `<b>${this.carrierDestinationPrice.priceLabel(this.carrier, this.currencies)}</b>`;
            return rangeMeaning;
        },
        formInvalid() {
            return !!this.conflict;
        },
    },
    methods: {
        show(carrierDestinationPrice = null) {
            this.mode = carrierDestinationPrice === null ? "create" : "edit";
            this.carrierDestinationPrice = new CarrierDestinationPrice(carrierDestinationPrice);
            this.hasStartingPrice = !!this.carrierDestinationPrice.startingPrice;
            // eslint-disable-next-line no-undef
            $("#carrier-destination-price-form-modal").modal("show");
            if (this.mode === "create") {
                setTimeout(() => {
                    if (this.carrier.chargesBy === "weight") {
                        this.$refs.minWeightInput.focus();
                    } else if (this.carrier.chargesBy === "quantity") {
                        this.$refs.minQuantityInput.focus();
                    }
                }, 500);
            }
        },
        async submit() {
            this.carrierDestinationPrice.carrierDestinationId = this.carrierDestination.id;
            try {
                this.loading = true;
                this.carrierDestinationPrice.sanitizeForAPI(this.carrier);
                if (this.mode === "create") {
                    await this.submitCreate();
                } else if (this.mode === "edit") {
                    await this.submitEdit();
                }
            } catch (e) {
                this.$displayError(e);
            } finally {
                this.loading = false;
            }
        },
        async submitCreate() {
            let response;
            if (this.carrierDestination.carrierAreaId) {
                response = await this.$diceAPI.postCarrierAreaPrice(this.carrierDestination.carrierAreaId, this.carrierDestinationPrice);
                this.$emit("editCarrierArea", response.data);
            } else {
                response = await this.$diceAPI.postCarrierDestinationPrice(this.carrierDestinationPrice);
                this.$emit("addCarrierDestinationPrice", response.data);
            }
            // eslint-disable-next-line no-undef
            $("#carrier-destination-price-form-modal").modal("hide");
            this.$toasted.success(this.$t("CarrierDestinationPriceFormModal.carrierDestinationPriceAdded"), { icon: "check" });
        },
        async submitEdit() {
            let response;
            if (this.carrierDestination.carrierAreaId) {
                response = await this.$diceAPI.patchCarrierAreaPrice(this.carrierDestination.carrierAreaId, this.carrierDestinationPrice);
                this.$emit("editCarrierArea", response.data);
            } else {
                response = await this.$diceAPI.patchCarrierDestinationPrice(this.carrierDestinationPrice);
                this.$emit("editCarrierDestinationPrice", response.data);
            }
            // eslint-disable-next-line no-undef
            $("#carrier-destination-price-form-modal").modal("hide");
            this.$toasted.success(this.$t("CarrierDestinationPriceFormModal.carrierDestinationPriceEdited"), { icon: "check" });
        },
        setConflict(conflict) {
            this.conflict = conflict;
        },
    },
    watch: {
        hasStartingPrice(value) {
            if (!value) {
                this.carrierDestinationPrice.startingPrice = null;
                this.carrierDestinationPrice.firstProductIncludedInStartingPrice = false;
            }
        },
    },
};
</script>

<style scoped>

</style>